import {
	AccountLevelEnum,
	AccountStatusEnum,
	AccountSubscriptionEnum,
	StatusEnum,
	TransactionTypeEnum,
	UserRole,
} from '@app/app.enum';
import * as constants from '@app/constants';
import * as models from '@app/models';
import { History } from 'history';
import * as React from 'react';
import { InjectedIntl } from 'react-intl';
import { match, RouteComponentProps } from 'react-router';
import { IAxiosCancellation, ILocationProps } from './core.models';
import { ISnackBarFeature } from './shared.models';

const defaultRateSet: models.IRateSet[] = [{
	id: '',
	name: '',
	carrier: '',
	rateSetType: '',
	startDate: null,
	endDate: null,
	isInternational: false,
}];

const defaultUSPSTierConfig: models.IUSPSTierConfig = {
	ePostagePermitNum: '',
	ePostageWebToolId: '',
	ePostageWebToolPassword: '',
	eVSPermitNum: '',
	eVSWebToolId: '',
	eVSWebToolPassword: '',
	id: '',
	masterMID: '',
	tier: '',
	ePostageMID: '',
	ePostageReturnsMID: '',
	eVSMID: '',
    consumerKey: '',
    consumerSecret: '',
	eVSReturnsMID: '',
	description: '',
	default: false,
	isGlobal: false,
	numberOfAssociatedAccounts: 0,
	ePostageWebtoolMID: '',
	eVSWebtoolMID: '',
	mailOwnerMID: ''
};

export interface IAdminCustomerProps extends models.ISnackBarFeature {
	intl: InjectedIntl;
	history: History<any>;
	location: ILocationProps;
	match: match;
}

interface ICustomerListRouteParams {
	levelType: string;
}

export interface ICustomerListProps extends IAxiosCancellation, RouteComponentProps<ICustomerListRouteParams> {
	intl: InjectedIntl;
}
export interface IAdminCustomerDetailsProps extends ISnackBarFeature {
	intl: InjectedIntl;
	location: ILocationProps;
	match: match;
	customerAccount: models.IAccount;
	updateAccountStatus: (status: AccountStatusEnum) => void;
	visibleAccountNumber: string;
	partnerVisibleAccountNum: string;
	shipmentCount: number;
}
export interface ICustomerListState {
	appData: models.IAccountAppResponseVM;
	open: boolean;
	openConfirmDialog: boolean;
	apiCredsToRegenerate: string;
	signUpUrl: string;
	changeCustomerEmail: boolean;
	data: IAdminTreeTableData[];
	tableColumnExtensions: IAdminTreeTableColumnExtensions[];
	expandedRowIds: string[];
	appId: string;
	sendingEmail: boolean;
	selectedAppToShare: models.IAccountAppResponseVM;
	dataLoading: boolean;
	selectedStatusFilterValues: string[];
	selectedSubscriptionsFilterValues: string[];
	customerSearch: string;
	partnerSearch: string;
	fetchData: boolean;
	disabledCheckBox: object;
	isOpenStatusFilter: boolean;
	isOpenSubscriptionsFilter: boolean;
	selectAllStatusSelection: boolean;
	selectAllSubscriptionSelection: boolean;
	selectedLevelTypeFilterValue: AccountLevelEnum;
	isPartnerFilter: boolean;
}

export interface IAdminAccountDetailsProps {
	intl: InjectedIntl;
	location: ILocationProps;
	match: match;
}

export const initialAdminAccountDetailsState = (): models.ICustomerAccountDetailsState => ({
	user: [],
	account: models.initialAccount(),
	visibleAccountNumber: '',
	partnerVisibleAccountNum: '',
	partnerUSPSTier: defaultUSPSTierConfig,
	status: AccountStatusEnum.Active,
	apps: [],
	uspsTierConfigs: [],
	uspsTierConfig: {
		selected: defaultUSPSTierConfig,
		current: defaultUSPSTierConfig,
	},
	rateSets: defaultRateSet,
	uniqueRateSetCarrier: [],
	buttonLoading: false,
	balance: 0,
	shipmentCount: 0,
	users: [],
	rateSetdataLoading: false,
	uspsConfigdataLoading: false,
	tierConfigDialogOpen: false,
});

export const NoneUSPSTierData = (): models.IUSPSTierConfig => ({
	ePostagePermitNum: '',
	ePostageWebToolId: '',
	ePostageWebToolPassword: '',
	eVSPermitNum: '',
	eVSWebToolId: '',
	eVSWebToolPassword: '',
	ePostageMID: '',
	ePostageReturnsMID: '',
	eVSMID: '',
	eVSReturnsMID: '',
    consumerKey: '',
    consumerSecret: '',
	ePostageWebtoolMID: '',
	eVSWebtoolMID: '',
	mailOwnerMID: '',
	description: '',
	id: 'none',
	masterMID: '',
	tier: 'None',
	default: false,
	isGlobal: false,
	numberOfAssociatedAccounts: 0
});

export const AddNewUSPSTierData = (): models.IUSPSTierConfig => ({
	ePostagePermitNum: '',
	ePostageWebToolId: '',
	ePostageWebToolPassword: '',
	eVSPermitNum: '',
	eVSWebToolId: '',
	eVSWebToolPassword: '',
	ePostageMID: '',
	ePostageReturnsMID: '',
	eVSMID: '',
	eVSReturnsMID: '',
    consumerKey: '',
    consumerSecret: '',
	ePostageWebtoolMID: '',
	eVSWebtoolMID: '',
	mailOwnerMID: '',
	description: '',
	id: 'add-new-tier',
	masterMID: '',
	tier: 'Add New Tier',
	default: false,
	isGlobal: false,
	numberOfAssociatedAccounts: 0
});

export interface IAdminTreeTableColumnExtensions {
	columnName: string;
	width: number;
}

export interface IAdminTreeTableColumn {
	// name: string;
	// title: string;
	Header: string;
	accessor?: string;
	id?: string;
	Cell?: unknown;
}

export interface IAdminTreeTableData {
	id: string;
	parentAccountId: string;
	childCount: number;
	companyName: string;
	email: string;
	name: string;
	shipmentCount: number;
	joiningDate: React.ReactNode;
	lastLoginDate: React.ReactNode;
	status: string;
	role: UserRole;
	visibleAccountNumber: string;
	partnerVisibleAccountNum: string;
	userName: string;
	backgroundColor?: string;
	subscriptionPlan?: AccountSubscriptionEnum;
}

export const initialAdminCustomerState = (
	userData: models.IUserSignInResponse,
	signUpUrl: string
): ICustomerListState => ({
	apiCredsToRegenerate: '',
	appData: userData.apps[0],
	openConfirmDialog: false,
	open: false,
	signUpUrl,
	changeCustomerEmail: false,
	appId: constants.defaultAppFilterValue,
	data: [],
	selectedStatusFilterValues: [],
	customerSearch: '',
	partnerSearch: '',
	fetchData: false,
	selectAllStatusSelection: true,
	disabledCheckBox: {},
	isOpenStatusFilter: false,
	tableColumnExtensions: [
		{ columnName: 'partnerVisibleAccountNum', width: 320 },
		{ columnName: 'companyName', width: 250 },
		{ columnName: 'email', width: 320 },
		{ columnName: 'status', width: 180 },
		{ columnName: 'role', width: 120 },
	],
	expandedRowIds: [userData.account.id],
	sendingEmail: false,
	selectedAppToShare: null,
	dataLoading: false,
	selectAllSubscriptionSelection: true,
	selectedSubscriptionsFilterValues: [],
	isOpenSubscriptionsFilter: false,
	selectedLevelTypeFilterValue: AccountLevelEnum.Customer,
	isPartnerFilter: false,
});

export interface IAdminCustomerProps extends IAxiosCancellation, models.ISnackBarFeature {
	intl: InjectedIntl;
}

export interface IService {
	id: string;
	code: string;
	name: string;
	status: StatusEnum;
	isInternational: boolean;
}

export interface IServiceUpdateRequest {
	id: string;
	status: StatusEnum;
}

export interface IPackageType {
	id: string;
	code: string;
	name: string;
	status: StatusEnum;
}

export interface ISpecialService {
	id: string;
	code: string;
	name: string;
	status: StatusEnum;
}
export interface IAdminCustomerPreviousFilters {
	selectedPartnerFilterValues?: string[];
	selectedStatusFilterValues?: string[];
	selectedSubscriptionsFilterValues?: string[];
	selectAllPartnerSelection?: boolean;
	selectAllStatusSelection?: boolean;
	selectAllSubscriptionSelection?: boolean;
	disabledCheckBox?: object;
}
export interface IAdminCustomerFilters {
	partnerVisibleAccountNum: string[];
	accountStatus: string[];
	accountSubscriptions: string[];
}

export const initialAdminCustomerFilter = (): IAdminCustomerFilters => ({
	partnerVisibleAccountNum: [],
	accountStatus: [],
	accountSubscriptions: [],
});

export interface IAdjustBalanceState {
	adjustloading: boolean;
	dataLoading: boolean;
	transactionType: TransactionTypeEnum;
	openDialog: boolean;
	amount: string;
	currentBalance: number;
	visibleAccountNumber: string;
}

export interface IAdjustBalanceFields {
	amount: number;
}

export interface IAdjustBalanceRequest {
	amount: string;
	accountId: string;
	transactionType: TransactionTypeEnum;
}

export interface IAdjustBalanceProps extends models.ISnackBarFeature {
	intl: InjectedIntl;
	match?: match;
	updateAccountBalance?: (accountBalance: number) => void;
	account?: models.IAccount;
	balance?: number;
	visibleAccountNumber?: string;
	isPartnerAccount?: boolean;
}

export interface IAdminDetailsState {
	companyName: string;
	email: string;
	name: string;
	shipmentCount: number;
	joiningDate: React.ReactNode;
	lastLoginDate: React.ReactNode;
	status: string;
	role: UserRole;
	visibleAccountNumber: string;
	partnerVisibleAccountNum: string;
	userName: string;
}

export interface IAdminAccountUserListProps extends IAxiosCancellation, models.ISnackBarFeature {
	intl: InjectedIntl;
	location: ILocationProps;
	match: match;
	accountId: string;
	accountStatus: AccountStatusEnum;
	users: IAdminAccountUserTreeTableData[];
}

export interface IAdminAccountUserListState {
	data: IAdminAccountUserTreeTableData[];
	dataLoading: boolean;
	isOpenDeleteDialog: boolean;
	isOpenEditDialog: boolean;
	isOpenResetDialog: boolean;
	email: string;
	userId: string;
	password: string;
	userName: string;
	passwordAdmin: string;
	passwordAdminExpiresOn: Date;
	isAdminPasswordVerificationDialogOpen: boolean;
	isGeneratedPasswordDialogOpen: boolean;
	isTempPasswordGeneratedLoading: boolean;
	editUserLoading: boolean;
	deleteUserLoading: boolean;
	resetUserLoading: boolean;
	collapse: {};
	isOpenAddUserDialog: boolean;
	firstName: string;
	lastName: string;
	addUserLoading: boolean;
	userSearchString: string;
	openInviteDialog: boolean;
	inviteId: string;
}

export const initialAdminAccountUserListState = (): models.IAdminAccountUserListState => ({
	data: [],
	dataLoading: false,
	isOpenDeleteDialog: false,
	isOpenEditDialog: false,
	isOpenResetDialog: false,
	email: '',
	userId: '',
	password: '',
	userName: '',
	passwordAdmin: '',
	passwordAdminExpiresOn: null,
	isAdminPasswordVerificationDialogOpen: false,
	isGeneratedPasswordDialogOpen: false,
	isTempPasswordGeneratedLoading: false,
	editUserLoading: false,
	deleteUserLoading: false,
	resetUserLoading: false,
	collapse: {},
	isOpenAddUserDialog: false,
	firstName: '',
	lastName: '',
	addUserLoading: false,
	userSearchString: '',
	openInviteDialog: false,
	inviteId: '',
});

export interface IAccountUserResponseVM {
	email: string;
	userName: string;
	name: string;
	id: string;
	userRole?: UserRole;
	lastLoginDate?: Date;
	joiningDate?: Date;
	isActivated?: boolean;
}

export interface IAdminAccountUserTreeTableData {
	email: string;
	name: string;
	userName: string;
	id: string;
	userRole?: UserRole;
	lastLoginDate?: Date;
	joiningDate?: Date;
	isActivated?: boolean;
}

export interface IUpdateUserEmail {
	email: string;
}

export interface IAdminPasswordVerificationRequest {
	passwordAdmin: string;
	id: string;
}
export interface IGenerateTempPasswordResponse {
	id: string;
	email: string;
	userName: string;
	passwordAdmin: string;
	passwordAdminExpiresOn: Date;
}

export interface IAccountServiceState {
	accountId?: string;
	companyName: string;
	visibleAccountNumber: string;
	serviceState: boolean;
}



// WEBPACK FOOTER //
// ./src/models/admin-customer.models.ts